import React, { useState, useEffect } from 'react';
import api from '../services/api';
import './OrderEntry.css';

function OrderEntry({ onBack, addToCart, cart, setCart }) {
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState('');
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [search, setSearch] = useState('');
  const [remarks, setRemarks] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const companyID = localStorage.getItem('companyID');
      const [custRes, prodRes] = await Promise.all([
        api.getCustomers(companyID),
        api.getProducts(companyID)
      ]);
      setCustomers(custRes.data.customers || []);
      setProducts(prodRes.data.products || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const updateQuantity = (productID, delta) => {
    setCart(prev => prev.map(item =>
      item.productID === productID
        ? { ...item, quantity: Math.max(1, item.quantity + delta) }
        : item
    ));
  };

  const removeFromCart = (productID) => {
    setCart(prev => prev.filter(item => item.productID !== productID));
  };

  const getSubtotal = () => cart.reduce((sum, item) => sum + (item.unitPrice * item.quantity), 0);
  const getTax = () => 0; // modify if tax logic is needed
  const getTotal = () => getSubtotal() + getTax();

  const submitOrder = async () => {
    if (!selectedCustomer) {
      alert('Please select a customer');
      return;
    }
    if (cart.length === 0) {
      alert('Cart is empty');
      return;
    }

    setSubmitting(true);
    try {
      const companyID = localStorage.getItem('companyID');
      const salesmanID = localStorage.getItem('employeeID') || 'EMP001';

      const orderData = {
        companyID,
        customerID: selectedCustomer,
        salesmanID,
        items: cart.map(item => ({
          productID: item.productID,
          quantity: item.quantity,
          unit_price: item.unitPrice
        })),
        subtotal: getSubtotal(),
        tax_amount: getTax(),
        total_amount: getTotal(),
        remarks
      };

      const response = await api.createOrder(orderData);
      if (response.data.success) {
        alert('Order submitted successfully!');
        setCart([]);
        setSelectedCustomer('');
        setRemarks('');
      } else {
        alert('Failed to submit: ' + (response.data.error || 'Unknown error'));
      }
    } catch (err) {
      alert('Failed to submit order: ' + (err.response?.data?.error || err.message));
    } finally {
      setSubmitting(false);
    }
  };

  const filteredProducts = products.filter(p =>
    p.productName.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) return <div className="order-loading">Loading...</div>;

  return (
    <div className="order-container">
      <div className="order-header">
        <button className="back-btn" onClick={onBack}>← Back to Dashboard</button>
        <h1>New Sales Order</h1>
      </div>

      <div className="order-content">
        {/* Customer Selection */}
        <div className="customer-section">
          <h3>Select Customer</h3>
          <select
            value={selectedCustomer}
            onChange={(e) => setSelectedCustomer(e.target.value)}
            className="customer-select"
          >
            <option value="">-- Select Customer --</option>
            {customers.map(c => (
              <option key={c.customerID} value={c.customerID}>
                {c.customerName} - Outstanding: RM {(c.outstandingBalance || 0).toFixed(2)}
              </option>
            ))}
          </select>
        </div>

        {/* Product Search */}
        <div className="product-search">
          <input
            type="text"
            placeholder="Search products to add..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="product-search-input"
          />
        </div>

        {/* Available Products */}
        <div className="available-products">
          <h3>Products</h3>
          <div className="products-list">
            {filteredProducts.map(product => (
              <div key={product.productID} className="product-item">
                <div className="product-details">
                  <span className="product-name">{product.productName}</span>
                  <span className="product-price">RM {product.unitPrice}</span>
                  <span className="product-stock">Stock: {product.stockLevel ?? 0}</span>
                </div>
                <button
                  className="add-btn"
                  onClick={() => addToCart(product)}
                  disabled={product.stockLevel <= 0}
                >
                  +
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Shopping Cart */}
        <div className="cart-section">
          <h3>Shopping Cart ({cart.length} items)</h3>
          {cart.length === 0 ? (
            <div className="empty-cart-msg">Cart is empty</div>
          ) : (
            <>
              <div className="cart-items">
                {cart.map(item => (
                  <div key={item.productID} className="cart-item">
                    <div className="cart-item-info">
                      <span className="cart-item-name">{item.productName}</span>
                      <span className="cart-item-price">RM {item.unitPrice}</span>
                    </div>
                    <div className="cart-item-controls">
                      <button onClick={() => updateQuantity(item.productID, -1)}>-</button>
                      <span>{item.quantity}</span>
                      <button onClick={() => updateQuantity(item.productID, 1)}>+</button>
                      <button className="remove" onClick={() => removeFromCart(item.productID)}>🗑️</button>
                    </div>
                  </div>
                ))}
              </div>
              <div className="cart-total">
                <span>Total:</span>
                <span className="total-amount">RM {getTotal().toFixed(2)}</span>
              </div>
              <div className="remarks-section">
                <label>Remarks (optional):</label>
                <textarea
                  value={remarks}
                  onChange={(e) => setRemarks(e.target.value)}
                  rows="2"
                  placeholder="Add any notes..."
                />
              </div>
              <button
                className="submit-order-btn"
                onClick={submitOrder}
                disabled={submitting || cart.length === 0 || !selectedCustomer}
              >
                {submitting ? 'Submitting...' : 'Submit Order'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default OrderEntry;